import React from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { Box, Card, Stack, Avatar, Tooltip, Checkbox, ListItem, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { IMonthlyData, IMilestoneSelected } from '../types';
import { ChoicesOrderItemType } from '@/sections/administrative/accounts/orders/types';
import { MilestoneStatusLabel } from '@components/core/milestone-status-label';
import { RHFNumberText } from '@components/forms/hook-form/rhf-number-field';
import { ChoicesOrderItemLabel, ChoicesStatusMilestone } from '@/types';
import Label from '@components/core/label';

interface IMonthContentProps {
  monthData: IMonthlyData;
  selectedMilestones: IMilestoneSelected[];
  expandedSubscriptions: Set<string>;
  isMilestoneAvailable: (milestone: IMilestoneSelected) => boolean;
  onToggleMilestone: (milestone: IMilestoneSelected) => void;
  onToggleSubscriptionExpanded: (subscriptionId: string) => void;
  inUseMilestones?: string[];
}

export function MonthContent({
  monthData,
  selectedMilestones,
  expandedSubscriptions,
  isMilestoneAvailable,
  onToggleMilestone,
  onToggleSubscriptionExpanded,
  inUseMilestones = [],
}: IMonthContentProps) {
  const theme = useTheme();
  const milestones = monthData.milestones || [];

  if (milestones.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t('No milestones for this month')}
      </Typography>
    );
  }

  const monthMilestonesBySubscription: Record<
    string,
    { milestones: IMilestoneSelected[]; name: string }
  > = {};
  milestones.forEach((milestone: IMilestoneSelected) => {
    const subscriptionId =
      milestone.subscription?._id || milestone.orderItem?.subscriptionId || undefined;
    if (!subscriptionId) return;
    if (!monthMilestonesBySubscription[subscriptionId]) {
      monthMilestonesBySubscription[subscriptionId] = {
        milestones: [],
        name: subscriptionId,
      };
    }
    monthMilestonesBySubscription[subscriptionId].milestones.push(milestone);
  });

  return (
    <>
      {Object.entries(monthMilestonesBySubscription).map(([subscription, { milestones: list }]) => {
        const accordionId = `${monthData._id}-${subscription}`;
        const isExpanded = expandedSubscriptions.has(accordionId);

        return (
          <Box key={accordionId} sx={{ mb: 2 }}>
            <Card
              variant="outlined"
              onClick={() => onToggleSubscriptionExpanded(accordionId)}
              sx={{ cursor: 'pointer', mb: isExpanded ? 2 : 0 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontWeight="bold">{list[0]?.orderItem?.sku?.name}</Typography>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'grey.200',
                      color: 'text.secondary',
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {list.length}
                  </Avatar>
                </Stack>
                <ExpandMoreIcon
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </Stack>
            </Card>

            {isExpanded && (
              <Card
                sx={{
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                  borderRadius: 2,
                  boxShadow: theme.customShadows?.z8,
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                    backgroundColor: 'background.neutral',
                  }}
                >
                  <Box width={40} />
                  <Typography variant="caption" fontWeight="bold" width={120}>
                    {t('Billing Date')}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" width={220}>
                    {t('Product/SKU/Plan Code')}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" width={140}>
                    {t('Type')}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" width={100}>
                    {t('Amount')}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" width={80}>
                    {t('Status')}
                  </Typography>
                </Stack>

                {list.map((milestone, idx) => {
                  const isInUse = inUseMilestones.includes(milestone._id);
                  return (
                    <Tooltip
                      key={milestone._id}
                      title={
                        isInUse
                          ? t('This milestone is being used recently in another billing order')
                          : ''
                      }
                      arrow
                      disableHoverListener={!isInUse}
                    >
                      <ListItem
                        dense
                        disableGutters
                        sx={{
                          borderBottom:
                            idx < list.length - 1
                              ? `1px solid ${alpha(theme.palette.grey[500], 0.08)}`
                              : 'none',
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                          <Checkbox
                            checked={selectedMilestones.some(
                              (m: IMilestoneSelected) => m._id === milestone._id
                            )}
                            onChange={() => onToggleMilestone(milestone)}
                            disabled={isInUse}
                          />
                          <Typography width={120} variant="body2">
                            {moment(milestone.paymentDate).utc().format('L')}
                          </Typography>
                          <Box width={220}>
                            <Typography variant="subtitle2" noWrap>
                              {milestone.orderItem?.product?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {milestone.orderItem?.plan?.name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {milestone.orderItem?.plan?.code ?? '-'}
                            </Typography>
                          </Box>
                          <Box width={140}>
                            <Label
                              color={
                                milestone.orderItem?.orderItemType ===
                                ChoicesOrderItemType.newResource
                                  ? 'primary'
                                  : 'secondary'
                              }
                              variant="outlined"
                            >
                              {ChoicesOrderItemLabel[milestone.orderItem?.orderItemType]}
                            </Label>
                          </Box>
                          <Typography width={100} fontWeight="bold">
                            <RHFNumberText
                              value={milestone.total}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix="$"
                            />
                          </Typography>
                          <Box width={80}>
                            <MilestoneStatusLabel
                              status={milestone.status as ChoicesStatusMilestone}
                            />
                          </Box>
                        </Stack>
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </Card>
            )}
          </Box>
        );
      })}
    </>
  );
}
